import {
  memo,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { ChartDataFragment } from "@/__generated__/graphql";
import { FileChip } from "@/components/molecules/FileChip";
import { Chart } from "@/components/organisms/Chart";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import {
  buildContentTree,
  messageIsWorkflowStep,
  type UnifiedContentItem,
} from "@/components/organisms/ToolCallCard/ToolCallCard.tools";
import { useStickyBarBottom } from "@/components/organisms/ToolCallCard/ToolCallCard.hooks";
import {
  getSenderLabel,
  ThreadMessageBody,
} from "@/components/organisms/ToolCallCard/ToolCallCardMessageBody";

import { useResizeObserver } from "@/hooks/useResizeObserver";
import { Format } from "@/utils/format";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import type { MessageItem } from "./Thread.types";

/** How far below the top of the thread's scroll area a pinned prompt sticks. */
const PINNED_PROMPT_TOP = 12;

export type ThreadTurnPromptProps = {
  message: MessageItem | null;
  loading: boolean;
  turnIndex: number;
  /** Bordered card + sticky at top of the thread scroll area while in view. */
  isPinned: boolean;
  isStreaming?: boolean;
  /**
   * Where this turn stands. Its rows inherit it: only a ``live`` turn draws
   * anything as working, and a ``stopped`` one says so without waiting for
   * the run to write it.
   */
  turnState?: "live" | "stopped" | "ended";
  /**
   * What the thread is laid over, for the band a pinned prompt paints above
   * itself. The panel and the detail view sit on different surfaces, so the
   * host is the only one that knows which colour hides what scrolls past.
   */
  stickyBackdropColor?: string;
};

function ThreadTurnPromptInner({
  message,
  loading,
  turnIndex,
  isPinned,
  isStreaming = false,
  turnState = "live",
  stickyBackdropColor = "var(--background)",
}: ThreadTurnPromptProps) {
  // What the person sent, held to a height: taller, it fades out at the
  // bottom, and a click on it lets it scroll there instead.
  const sentBoxRef = useRef<HTMLDivElement | null>(null);
  const [isSentTall, setIsSentTall] = useState(false);
  const [isSentOpened, setIsSentOpened] = useState(false);
  // Opened and scrolled through, which edges have more past them: faded there,
  // and only there, so nothing is cut off sharp.
  const [sentEdges, setSentEdges] = useState({ above: false, below: false });
  const measureSentEdges = () => {
    const box = sentBoxRef.current;
    if (!box) return;
    const above = box.scrollTop > 1;
    const below = box.scrollTop + box.clientHeight < box.scrollHeight - 1;
    setSentEdges((was) =>
      was.above === above && was.below === below ? was : { above, below },
    );
  };
  const sentTextRef = useResizeObserver((rect) => {
    const box = sentBoxRef.current;
    setIsSentTall(box != null && rect.height > box.clientHeight + 1);
    measureSentEdges();
  });

  // The list prop is already the cache: streamed rows are written under the
  // identifiers the query reads, so there is no second copy to prefer and no
  // reason to hold the last non-empty tree to cover a gap between them.
  const treeContent = useMemo((): UnifiedContentItem[] => {
    const content = message?.content;
    if (!Array.isArray(content)) return [];
    // A row is only live because its turn is. The rows the server holds open
    // while a stopped run winds down — and any a dead run left behind — are
    // read as finished here, so nothing downstream can draw them as working.
    const rows =
      turnState === "live"
        ? (content as unknown as UnifiedContentItem[])
        : (content as unknown as UnifiedContentItem[]).map((row) =>
            row.status === "running" ? { ...row, status: "done" } : row,
          );
    // A stopped turn says so from the click. The run writes the same note when
    // it winds down seconds later, so the turn carries whichever it has — one
    // or the other, never both.
    if (turnState === "stopped" && !rows.some((row) => row.type === "notice")) {
      const identifier = `${message?.identifier ?? "turn"}-interrupted`;
      return buildContentTree([
        ...rows,
        {
          __typename: "MessageContentObject",
          id: identifier,
          identifier,
          order: Number.MAX_SAFE_INTEGER,
          type: "notice",
          status: "done",
          text: "Interrupted.",
        } as unknown as UnifiedContentItem,
      ]);
    }
    return buildContentTree(rows);
  }, [message?.content, message?.identifier, turnState]);

  const senderLabel = useMemo(
    () => getSenderLabel(message?.account, treeContent),
    [message?.account, treeContent],
  );
  // The panel query runs with ``returnPartialData``, so a row's content can
  // read back empty for a frame; the turn in flight stays on screen through
  // that rather than blinking out of the conversation.
  const showBody = treeContent.length > 0 || loading || isStreaming;

  // The turn's tool calls stick just under its pinned prompt, so the pin
  // segment they share is told where the prompt ends as it grows or shrinks.
  // A pixel short: what sticks under the prompt tucks just beneath its edge
  // rather than leaving a sliver for the thread to show through. The prompt
  // sits above it, so the overlap never shows.
  const pinnedRef = useStickyBarBottom(
    "--thread-pinned-prompt-bottom",
    undefined,
    PINNED_PROMPT_TOP - 1,
  );

  if (!showBody) return null;

  const isWorkflowStep = messageIsWorkflowStep(message);
  /** Sticky workflow steps: bubble wraps prompt only; tool tail scrolls outside. */
  const splitPromptFromTail = isPinned && isWorkflowStep;

  /** The turn's column: what was sent, and what came of it, one under another. */
  const promptShellBaseClass =
    "flex w-full min-h-0 min-w-0 gap-2 flex-col items-start justify-start pl-3 pr-3 pt-2 pb-2 self-stretch h-min";
  const promptShellStyle = {
    anchorName: `--turn-${turnIndex}-agent`,
  } as CSSProperties;

  /**
   * Pinned: the bubble itself sticks, inset from the top of the scroll area.
   * The inset would leave a gap for messages to show through above the card,
   * so it carries its own backdrop: a band painted directly above it, in the
   * page colour, reaching past the column's padding. Sitting outside the card's
   * border box, it covers what scrolls by without touching the card's own
   * border, corners or shadow.
   */
  const pinnedBubbleClass = cn(
    promptShellBaseClass,
    "[border-width:1px] [border-style:solid] border-border rounded-lg bg-background shadow-xs",
    "sticky top-(--pinned-prompt-top) z-(--pinned-prompt-z-index) shrink-0 mt-3 mb-2",
    // Absolute children sit against the padding box, so clear the card's own
    // top border rather than painting over it.
    "before:content-[''] before:absolute before:-left-4 before:-right-4 before:bottom-[calc(100%+1px)] before:h-5 before:bg-(--sticky-backdrop-color)",
  );
  const pinnedBubbleStyle = {
    ...promptShellStyle,
    "--pinned-prompt-top": `${PINNED_PROMPT_TOP}px`,
    "--pinned-prompt-z-index": turnIndex + 10,
    "--sticky-backdrop-color": stickyBackdropColor,
  } as CSSProperties;

  const chartFooter =
    message?.timeSeriesData != null ? (
      <>
        <div className="w-full h-px bg-border" />
        <Chart
          data={message.timeSeriesData as ChartDataFragment}
          type="bar"
          size="sm"
        />
      </>
    ) : null;

  const promptLabel = (
    <div
      className={cn(
        message?.account == null ? "mt-2" : "mt-0",
        "w-full flex items-baseline justify-between gap-2",
      )}
    >
      <Typography level="body-xs" className="text-muted-foreground/80">
        {senderLabel}
      </Typography>
      {message?.createdAt && (
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 shrink-0"
        >
          {Format.fTime(new Date(message.createdAt))}
        </Typography>
      )}
    </div>
  );

  const awaitingStreamContent = isStreaming && treeContent.length === 0;
  // What the user sent the message about. Without it "tell me about this"
  // reads back later with nothing to say what "this" was.
  const references =
    message?.account != null
      ? (
          ((message as { references?: unknown[] }).references ?? []) as Array<{
            node?: {
              __typename?: string;
              id?: string;
              identifier?: string;
              name?: string | null;
              description?: string | null;
            } | null;
          } | null>
        ).flatMap((context) => (context?.node?.id ? [context.node] : []))
      : [];
  // The files the user attached, drawn as they were on the composer.
  const files = message?.account != null ? (message.files ?? []) : [];
  const messageBody = (
    <ThreadMessageBody
      content={treeContent}
      isStreaming={isStreaming}
      account={message?.account}
      turnIndex={turnIndex}
      part={splitPromptFromTail ? "prompt" : "full"}
      startedAt={message?.createdAt}
    />
  );
  const isFaded = isSentTall && !isSentOpened;
  // Faded at its foot while shut; opened, at each edge there is more past.
  const fadesAbove = isSentOpened && sentEdges.above;
  const fadesBelow = isFaded || (isSentOpened && sentEdges.below);
  const sentMask =
    fadesAbove || fadesBelow
      ? `linear-gradient(to bottom, ${fadesAbove ? "transparent, #000 2rem" : "#000"}, ${fadesBelow ? "#000 calc(100% - 3rem), transparent" : "#000"})`
      : undefined;
  const promptBody = (
    <>
      {promptLabel}
      {message?.account != null ? (
        // What the person sent - its words, what it was about and the files
        // with it - no taller than a quarter of the screen: longer, it fades
        // out at the bottom, and clicked, it scrolls there.
        <div
          ref={sentBoxRef}
          data-testid="thread-turn-sent-text"
          data-faded={isFaded || undefined}
          data-edges={
            [fadesAbove && "above", fadesBelow && "below"]
              .filter(Boolean)
              .join(" ") || undefined
          }
          onClick={isFaded ? () => setIsSentOpened(true) : undefined}
          onScroll={measureSentEdges}
          className={cn(
            // Out to the bubble's right edge, so its scrollbar sits against
            // it; the words keep the bubble's padding inside.
            "self-stretch -mr-3 pr-3",
            // As tall as what the person sent is shown before it fades: a
            // quarter of the screen, at most 400px.
            "max-h-[min(25vh,400px)]",
            isSentOpened ? "overflow-y-auto" : "overflow-y-hidden",
            isFaded && "cursor-pointer",
            sentMask &&
              "[mask-image:var(--sent-mask)] [-webkit-mask-image:var(--sent-mask)]",
          )}
          style={{ "--sent-mask": sentMask } as CSSProperties}
        >
          <div ref={sentTextRef} className="flex flex-col gap-2">
            {messageBody}
            {references.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {references.map((node) => (
                  <div key={node.id} data-testid="thread-turn-reference">
                    <FieldReference
                      value={{
                        __typename: node.__typename,
                        id: node.id,
                        identifier: node.identifier,
                        name: node.name ?? node.description ?? "",
                      }}
                      valueOptions={{ style: "related", isNullable: false }}
                      isMultiline={false}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            )}
            {files.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {files.map((file) => (
                  <div key={file.id} data-testid="thread-turn-file">
                    {/* The same file the thread's own rows show, so looking at what
                        you sent shows the same thing wherever it is named. */}
                    <FileChip id={file.id} name={file.name ?? ""} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        messageBody
      )}
    </>
  );

  const renderPinnedPrompt = (body: ReactNode) => (
    <div
      data-testid="thread-turn"
      ref={pinnedRef}
      className={pinnedBubbleClass}
      style={pinnedBubbleStyle}
    >
      {body}
    </div>
  );

  /** Pinned: sticky shell is a direct child of the pin segment (tall scroll span). */
  if (isPinned && splitPromptFromTail) {
    return (
      <>
        {renderPinnedPrompt(promptBody)}
        <div className="w-full flex flex-col gap-2 pl-3 pr-3">
          <ThreadMessageBody
            content={treeContent}
            isStreaming={isStreaming}
            account={message?.account}
            turnIndex={turnIndex}
            part="tail"
          />
          {chartFooter}
        </div>
      </>
    );
  }

  if (isPinned) {
    return renderPinnedPrompt(promptBody);
  }

  return (
    <div data-testid="thread-turn" className="relative w-full">
      <div
        className={cn(promptShellBaseClass, "relative")}
        style={promptShellStyle}
      >
        {promptBody}
      </div>
    </div>
  );
}

export const ThreadTurnPrompt = memo(ThreadTurnPromptInner, (prev, next) => {
  if (prev.isStreaming || next.isStreaming) return false;
  // The cache hands back a new message object whenever anything in it changes
  // - a streamed row, the finished reply - and the same one when nothing did,
  // so identity is the change check. Comparing identifiers kept a turn on the
  // first streamed word once it stopped being flagged as streaming.
  return (
    prev.message === next.message &&
    prev.loading === next.loading &&
    prev.turnIndex === next.turnIndex &&
    prev.isPinned === next.isPinned &&
    prev.turnState === next.turnState &&
    prev.stickyBackdropColor === next.stickyBackdropColor
  );
});
